import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { functions } from '../../firebase';
import { AppState } from 'store';
import HeaderBar from 'Layout/HeaderBar';
import InventoryNav from './InventoryNav';
import { inventoryItemSchema } from 'modules/inventory/sensorInventory';
import { sensorDetailPath } from 'Views/SensorOverview/model';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';

const linkSchema = z.object({
  orgId: z.string(),
  sensorId: z.string(),
  inventoryId: z.string().nullable(),
  simId: z.string().nullable(),
  syncIssue: z.string().optional(),
  sensorName: z.string().optional(),
  sensorType: z.string().optional(),
  deviceId: z.string().optional(),
});
const schema = z.object({
  items: z.array(inventoryItemSchema),
  links: z.array(linkSchema),
  sensorTypes: z.array(z.string()),
});
const names: Record<string, string> = {
  MACHINE_ACTIVITY: 'Maskinaktivitet',
  ESP_AQ: 'AQ',
  ESP_AQ_NEW: 'AQ (ny)',
  foobot: 'Foobot',
  GSM_PLANT: 'GSM-anlæg',
  GSM_CUSTOM: 'GSM custom',
  GPS_SIMTRACKING: 'GPS Simtracking',
  'geolocation_GY-NEO6MV2': 'GPS',
  LEVEL_SENSOR_INTERNAL: 'Niveausensor',
  PLANT_MODULE: 'Plant module',
  dust_DSM501A: 'Støv (ældre)',
  simcards: 'SIM-kort',
  IPAD: 'iPad',
  pumps: 'Pumpe',
};
const setupTypes = new Set([
  'MACHINE_ACTIVITY',
  'ESP_AQ',
  'foobot',
  'GSM_PLANT',
  'GPS_SIMTRACKING',
  'geolocation_GY-NEO6MV2',
  'LEVEL_SENSOR_INTERNAL',
  'PLANT_MODULE',
]);
const inventoryRoutes = new Set([
  'MACHINE_ACTIVITY',
  'ESP_AQ',
  'foobot',
  'geolocation_GY-NEO6MV2',
  'simcards',
  'IPAD',
  'pumps',
]);

export default function SensorRegister() {
  const uid = useSelector((state: AppState) => state.user.details.uid);
  const { search: locationSearch } = useLocation();
  const [search, setSearch] = useState(new URLSearchParams(locationSearch).get('id') || '');
  const [filter, setFilter] = useState('sensors');
  const [page, setPage] = useState(0);
  const searchId = useId();
  const filterId = useId();
  const query = useQuery({
    queryKey: ['inventory-sensor-register', uid],
    queryFn: async () => {
      const result = await functions.httpsCallable('settings')({
        requestType: 'GET_INVENTORY_SENSOR_OVERVIEW',
        payload: {},
      });
      return schema.parse(result.data);
    },
    staleTime: 0,
    refetchInterval: 30_000,
  });
  const data = query.data;
  const linksByInventory = new Map(
    data?.links.filter((link) => link.inventoryId).map((link) => [link.inventoryId!, link])
  );
  const rows = data
    ? [
        ...data.items.map((item) => ({ key: item.id, item, link: linksByInventory.get(item.id) })),
        ...data.links
          .filter(
            (link) => !link.inventoryId || !data.items.some((item) => item.id === link.inventoryId)
          )
          .map((link) => ({ key: `${link.orgId}/${link.sensorId}`, item: null, link })),
      ]
    : [];
  const filtered = rows.filter(({ key, item, link }) => {
    const type = item?.type || link?.sensorType || '';
    const sensor = Boolean(link) || Boolean(data?.sensorTypes.includes(type));
    return (
      (filter === 'all' ||
        (filter === 'sensors'
          ? sensor
          : filter === 'conflicts'
          ? Boolean(link?.syncIssue)
          : type === filter)) &&
      [
        key,
        item?.name,
        item?.identifier,
        item?.internalId,
        item?.iccid,
        item?.phoneNumber,
        item?.orgTitle,
        link?.sensorName,
        link?.deviceId,
        type,
      ].some((value) =>
        (value || '').toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
      )
    );
  });
  const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / 30) - 1));
  return (
    <>
      <HeaderBar />
      <main className="mx-auto max-w-[1600px] space-y-4 p-4 md:p-6">
        <InventoryNav />
        <h1 className="text-2xl font-semibold">Sensorer og lagerkoblinger</h1>
        <p>
          Alle sensortyper og deres lagerdele. SIM-kort og øvrigt lager bevarer deres egne
          kategorier. Automatisk synkronisering kan tage et øjeblik; konflikter kræver kontrol.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-64 flex-1">
            <label htmlFor={searchId}>Søg efter enhed, internt ID, SIM eller kunde</label>
            <Input
              id={searchId}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
            />
          </div>
          <div>
            <label htmlFor={filterId} className="block">
              Vis
            </label>
            <select
              id={filterId}
              className="rounded-md border bg-white p-2"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value);
                setPage(0);
              }}
            >
              <option value="sensors">Alle sensortyper</option>
              <option value="all">Alle lagerdele inkl. SIM-kort</option>
              <option value="conflicts">Kræver kontrol</option>
              {Object.entries(names).map(([type, name]) => (
                <option key={type} value={type}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="outline"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Opdatér
          </Button>
        </div>
        {query.isPending && <p role="status">Henter lager og sensorkoblinger…</p>}
        {query.isError && (
          <p role="alert">Kunne ikke opdatere lagerkoblingerne. Prøv Opdatér igen.</p>
        )}
        {data && (
          <>
            <p>
              {filtered.length} poster · {data.links.filter((link) => link.syncIssue).length} kræver
              kontrol
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Enhed / lagerdel</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Kobling</TableHead>
                  <TableHead>Handling</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered
                  .slice(currentPage * 30, currentPage * 30 + 30)
                  .map(({ key, item, link }) => {
                    const type = item?.type || link?.sensorType || '';
                    const orgId = link?.orgId || item?.orgId;
                    const path = link
                      ? sensorDetailPath(link.orgId, link.sensorId, link.sensorType || type)
                      : null;
                    const sim = link?.simId
                      ? data.items.find((entry) => entry.id === link.simId)
                      : null;
                    const setup =
                      item && !link && orgId && item.identifier && setupTypes.has(type) &&
                      !data.links.some(
                        (registration) => registration.sensorType === type &&
                          registration.deviceId === item.identifier
                      )
                        ? `/orgs/${encodeURIComponent(orgId)}/sensors?${new URLSearchParams({
                            setupType: type,
                            deviceId: item.identifier,
                            title: item.name || item.identifier,
                          })}`
                        : null;
                    return (
                      <TableRow key={key}>
                        <TableCell>
                          <div className="font-medium">
                            {item?.name ||
                              link?.sensorName ||
                              item?.identifier ||
                              item?.iccid ||
                              'Ukendt'}
                          </div>
                          <div>{item?.identifier || link?.deviceId || item?.iccid}</div>
                          {item?.internalId && <div>Internt ID: {item.internalId}</div>}
                          {item?.phoneNumber && <div>{item.phoneNumber}</div>}
                          {sim && (
                            <div>
                              SIM: {sim.iccid} · {sim.phoneNumber || 'Mobilnummer mangler'}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{names[type] || type || 'Ukendt type'}</TableCell>
                        <TableCell>{item?.orgTitle || orgId || 'På lager / uden kunde'}</TableCell>
                        <TableCell>
                          {link?.syncIssue ? (
                            <span className="text-amber-800">{link.syncIssue}</span>
                          ) : link?.inventoryId ? (
                            'Tilknyttet sensor'
                          ) : data.sensorTypes.includes(type) ? (
                            'Ingen sensorkobling'
                          ) : (
                            'Lagerdel'
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-2">
                            {path && (
                              <Link className="text-sky-700 underline" to={path}>
                                Åbn sensor og kobling
                              </Link>
                            )}
                            {setup && (
                              <Link className="text-sky-700 underline" to={setup}>
                                Opsæt sensor
                              </Link>
                            )}
                            {item && inventoryRoutes.has(type) && (
                              <Link
                                className="text-sky-700 underline"
                                to={`/inventory/${encodeURIComponent(type)}?id=${encodeURIComponent(
                                  item.id
                                )}`}
                              >
                                Åbn lagerdel
                              </Link>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>Ingen poster matcher søgningen.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                disabled={currentPage === 0}
                onClick={() => setPage(currentPage - 1)}
              >
                Forrige
              </Button>
              <span>
                Side {currentPage + 1} af {Math.max(1, Math.ceil(filtered.length / 30))}
              </span>
              <Button
                variant="outline"
                disabled={(currentPage + 1) * 30 >= filtered.length}
                onClick={() => setPage(currentPage + 1)}
              >
                Næste
              </Button>
            </div>
          </>
        )}
      </main>
    </>
  );
}
